import { useState, useEffect, useMemo } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Badge,
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Progress,
} from 'debug-time-machine-ui';
import { 
  Activity,
  TrendingUp,
  TrendingDown,
  Users,
  Clock,
  AlertTriangle,
  CheckCircle2,
  BarChart3,
  Wifi,
  WifiOff,
  RefreshCw,
  Zap,
  Database,
  Globe
} from 'lucide-react';

interface MetricsDashboardProps {
  isConnected: boolean;
  clientId: string | null;
}

interface SystemMetrics {
  uptime: number;
  memoryUsage: number;
  cpuUsage: number;
  networkLatency: number;
  errorRate: number;
  throughput: number;
}

interface ConnectionStats {
  totalConnections: number;
  activeClients: number;
  averageResponseTime: number;
  dataTransferred: number;
  lastActivity: number;
}

const MetricsDashboard = ({ isConnected, clientId }: MetricsDashboardProps) => {
  const [metrics, setMetrics] = useState<SystemMetrics>({
    uptime: 0,
    memoryUsage: 0,
    cpuUsage: 0,
    networkLatency: 0,
    errorRate: 0,
    throughput: 0,
  });

  const [connectionStats, setConnectionStats] = useState<ConnectionStats>({
    totalConnections: 0,
    activeClients: 0,
    averageResponseTime: 0,
    dataTransferred: 0,
    lastActivity: Date.now(),
  });

  const [historicalData, setHistoricalData] = useState<SystemMetrics[]>([]);

  // 시뮬레이션된 메트릭 업데이트
  useEffect(() => {
    if (!isConnected) return;

    const interval = setInterval(() => {
      const newMetrics: SystemMetrics = {
        uptime: Date.now() - (Date.now() - 3600000), // 1시간 가정
        memoryUsage: 30 + Math.random() * 40, // 30-70%
        cpuUsage: 10 + Math.random() * 30, // 10-40%
        networkLatency: 50 + Math.random() * 100, // 50-150ms
        errorRate: Math.random() * 2, // 0-2%
        throughput: 1000 + Math.random() * 500, // 1000-1500 req/min
      };

      setMetrics(newMetrics);
      
      // 히스토리컬 데이터 업데이트 (최근 20개만 유지)
      setHistoricalData(prev => [...prev, newMetrics].slice(-20));

      // 연결 통계 업데이트
      setConnectionStats(prev => ({
        ...prev,
        activeClients: clientId ? 1 : 0,
        totalConnections: prev.totalConnections + (Math.random() > 0.8 ? 1 : 0),
        averageResponseTime: newMetrics.networkLatency,
        dataTransferred: prev.dataTransferred + Math.random() * 1024,
        lastActivity: Date.now(),
      }));
    }, 2000);

    return () => clearInterval(interval);
  }, [isConnected, clientId]);

  // 헬스 스코어 계산
  const healthScore = useMemo(() => {
    if (!isConnected) return 0;
    
    const scores = [
      Math.max(0, 100 - metrics.memoryUsage), // 메모리 사용량이 낮을수록 좋음
      Math.max(0, 100 - metrics.cpuUsage * 2), // CPU 사용량이 낮을수록 좋음
      Math.max(0, 100 - metrics.networkLatency / 2), // 지연시간이 낮을수록 좋음
      Math.max(0, 100 - metrics.errorRate * 50), // 에러율이 낮을수록 좋음
    ];
    
    return Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length);
  }, [metrics, isConnected]);

  const getHealthColor = (score: number) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const formatUptime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    return `${minutes}m ${seconds % 60}s`;
  };

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes.toFixed(0)}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  if (!isConnected) {
    return (
      <div className="text-center py-12">
        <WifiOff className="w-12 h-12 mx-auto mb-4 text-slate-400" />
        <h3 className="text-lg font-medium text-slate-600 dark:text-slate-300 mb-2">
          Not Connected
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          Connect to a debug server to view advanced metrics
        </p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry Connection
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 시스템 상태 개요 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">System Health</CardTitle>
            {healthScore >= 80 ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-amber-500" />
            )}
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${getHealthColor(healthScore)}`}>
              {healthScore}%
            </div>
            <p className="text-xs text-muted-foreground">
              Overall system performance
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Connection Status</CardTitle>
            <Wifi className="w-4 h-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">
              {connectionStats.activeClients}
            </div>
            <p className="text-xs text-muted-foreground">
              Active clients connected
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Uptime</CardTitle>
            <Clock className="w-4 h-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatUptime(metrics.uptime)}
            </div>
            <p className="text-xs text-muted-foreground">
              System running time
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="performance" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="network">Network</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="performance" className="space-y-4">
          {/* 성능 메트릭 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-orange-500" />
                  CPU Usage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Current</span>
                    <span className="text-sm text-muted-foreground">
                      {metrics.cpuUsage.toFixed(1)}%
                    </span>
                  </div>
                  <Progress value={metrics.cpuUsage} className="h-2" />
                  <p className="text-xs text-muted-foreground">
                    {metrics.cpuUsage < 20 ? 'Low usage' : 
                     metrics.cpuUsage < 50 ? 'Moderate usage' : 'High usage'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-blue-500" />
                  Memory Usage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Current</span>
                    <span className="text-sm text-muted-foreground">
                      {metrics.memoryUsage.toFixed(1)}%
                    </span>
                  </div>
                  <Progress value={metrics.memoryUsage} className="h-2" />
                  <p className="text-xs text-muted-foreground">
                    {metrics.memoryUsage < 30 ? 'Low usage' : 
                     metrics.memoryUsage < 70 ? 'Moderate usage' : 'High usage'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* 성능 트렌드 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
                Performance Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              {historicalData.length > 1 ? (
                <div className="space-y-4">
                  {/* CPU 트렌드 */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium">CPU Usage</span>
                      <span className="text-sm text-muted-foreground">Last 20 samples</span>
                    </div>
                    <div className="h-12 flex items-end justify-between gap-1">
                      {historicalData.map((data, index) => {
                        const height = (data.cpuUsage / 100) * 100;
                        return (
                          <div
                            key={index}
                            className="flex-1 bg-orange-200 dark:bg-orange-800 rounded-t"
                            style={{ height: `${Math.max(height, 5)}%` }}
                            title={`${data.cpuUsage.toFixed(1)}% CPU`}
                          />
                        );
                      })}
                    </div>
                  </div>

                  {/* 메모리 트렌드 */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium">Memory Usage</span>
                      <span className="text-sm text-muted-foreground">Last 20 samples</span>
                    </div>
                    <div className="h-12 flex items-end justify-between gap-1">
                      {historicalData.map((data, index) => {
                        const height = (data.memoryUsage / 100) * 100;
                        return (
                          <div
                            key={index}
                            className="flex-1 bg-blue-200 dark:bg-blue-800 rounded-t"
                            style={{ height: `${Math.max(height, 5)}%` }}
                            title={`${data.memoryUsage.toFixed(1)}% Memory`}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Collecting performance data...</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="network" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-cyan-500" />
                  Network Latency
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {metrics.networkLatency.toFixed(0)}ms
                </div>
                <p className="text-xs text-muted-foreground">
                  Average response time
                </p>
                <div className="mt-2">
                  <Badge variant={metrics.networkLatency < 100 ? 'secondary' : 'outline'}>
                    {metrics.networkLatency < 100 ? 'Good' : 'Slow'}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-purple-500" />
                  Throughput
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {metrics.throughput.toFixed(0)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Requests per minute
                </p>
                <div className="mt-2">
                  <Badge variant="secondary">
                    {metrics.throughput > 1200 ? 'High' : 'Normal'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Connection Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-lg font-bold">{connectionStats.totalConnections}</div>
                  <div className="text-xs text-muted-foreground">Total Connections</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold">{connectionStats.activeClients}</div>
                  <div className="text-xs text-muted-foreground">Active Clients</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold">{formatBytes(connectionStats.dataTransferred)}</div>
                  <div className="text-xs text-muted-foreground">Data Transferred</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold">
                    {Math.round((Date.now() - connectionStats.lastActivity) / 1000)}s
                  </div>
                  <div className="text-xs text-muted-foreground">Last Activity</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Error Rate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${metrics.errorRate < 1 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {metrics.errorRate.toFixed(2)}%
                </div>
                <p className="text-xs text-muted-foreground">
                  Current error rate
                </p>
                <div className="mt-2">
                  <Badge variant={metrics.errorRate < 1 ? 'secondary' : 'destructive'}>
                    {metrics.errorRate < 1 ? 'Good' : 'High'}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  Client Info
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm">Client ID:</span>
                    <span className="text-sm font-mono">{clientId || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Status:</span>
                    <Badge variant="secondary">Connected</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Connection Time:</span>
                    <span className="text-sm">{formatUptime(300000)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>System Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h4 className="font-medium mb-2">Debug Server</h4>
                  <div className="space-y-1 text-muted-foreground">
                    <p>Version: 1.0.0</p>
                    <p>Protocol: WebSocket</p>
                    <p>Port: 3001</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Frontend Client</h4>
                  <div className="space-y-1 text-muted-foreground">
                    <p>Framework: React 18</p>
                    <p>Build: Production</p>
                    <p>Last Updated: {new Date().toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MetricsDashboard;
